import React, { useState, useRef, useEffect } from 'react';
import { createConsumer } from '@rails/actioncable';
import { useIntl } from 'react-intl';
import SearchBar from '../../../../../ui/canon/SearchBar';
import ReplayTourButton from '../../Shared/ReplayTourButton/ReplayTourButton';

/**
 * SupportHelpDrawer - User-facing support widget
 * Ported pixel-perfect from design_experiments/polymet-support-help-app/src/polymet/components/support-help-drawer.tsx
 *
 * Views: help-home | article-detail | contact-support | conversation | requests (tab)
 *
 * Theming: Uses CSS variable tokens (--succession-*) for theme-aware colors.
 *
 * Props provided by SupportHelpDrawerContainer via GraphQL:
 *   - isAuthenticated (bool)
 *   - unreadCount (number)
 *   - helpArticles ([{ id, title, excerpt, content, category, icon }])
 *   - supportCategories ([{ value, label, hint }])
 *   - supportRequests ([{ id, subject, category, status, createdAt, lastMessage, conversation }])
 *   - onSubmitRequest (fn)
 *   - onSubmitLeadRequest (fn)
 *   - onSendMessage (fn)
 */

const iconPaths = {
  users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  'file-text': 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  'dollar-sign': 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
};

const SvgIcon = ({ name, className }) => {
  const d = iconPaths[name] || iconPaths['file-text'];
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {d.split(' M').map((seg, i) => (
        <path key={i} d={i === 0 ? seg : `M${seg}`} />
      ))}
    </svg>
  );
};

const StatusBadge = ({ status }) => {
  const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
  const statusMap = {
    open: { bg: 'rgba(var(--succession-success-rgb, 16,185,129), 0.1)', color: 'var(--succession-success, #10b981)' },
    pending: { bg: 'rgba(var(--succession-warning-rgb, 245,158,11), 0.1)', color: 'var(--succession-warning, #f59e0b)' },
    closed: { bg: 'var(--succession-bg-tertiary, #e5e7eb)', color: 'var(--succession-text-secondary, #6b7280)' },
  };
  const s = statusMap[status] || statusMap.closed;
  return (
    <span className={baseClasses} style={{ backgroundColor: s.bg, color: s.color }}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : ''}
    </span>
  );
};

// Convert category slugs to title case: "getting-started" → "Getting Started", "deal_room" → "Deal Room"
const formatCategoryName = (slug) =>
  slug ? slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const SupportHelpDrawer = ({
  isAuthenticated = true,
  currentUser,
  unreadCount = 0,
  helpArticles = [],
  supportCategories = [],
  supportRequests = [],
  initialSelectedRequestId,
  onDrawerOpen,
  onSubmitRequest,
  onSubmitLeadRequest,
  onSendMessage,
}) => {
  const intl = useIntl();
  // Default categories for unauthenticated leads (GraphQL query is skipped for them)
  const DEFAULT_LEAD_CATEGORIES = [
    { value: 'general_inquiry', label: 'General Inquiry' },
    { value: 'platform_question', label: 'Platform Question' },
    { value: 'registration_help', label: 'Registration Help' },
    { value: 'other', label: 'Other' },
  ];

  // Safety fallback for authenticated users if supportCategories fails to hydrate.
  const DEFAULT_AUTH_CATEGORIES = [
    { value: 'account', label: 'Account & Settings' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'other', label: 'Other' },
  ];

  const effectiveCategories = supportCategories.length > 0
    ? supportCategories
    : (isAuthenticated ? DEFAULT_AUTH_CATEGORIES : DEFAULT_LEAD_CATEGORIES);

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('help-home');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [activeTab, setActiveTab] = useState('help');
  const [searchQuery, setSearchQuery] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [category, setCategory] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [files, setFiles] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [localMessages, setLocalMessages] = useState([]);
  const [realtimeMessages, setRealtimeMessages] = useState([]);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const realtimeRef = useRef([]);
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const cableConsumer = useRef(null);
  const cableSubscription = useRef(null);

  const prevRequestCount = useRef(supportRequests.length);
  const deepLinkHandled = useRef(false);

  // On mount (or when deep-linked request arrives in the list), open the drawer
  // directly to the referenced conversation.
  useEffect(() => {
    if (!initialSelectedRequestId || deepLinkHandled.current) return;
    const match = supportRequests.find((r) => String(r.id) === String(initialSelectedRequestId));
    if (!match) return; // wait until the record is in the list
    deepLinkHandled.current = true;
    setIsOpen(true);
    setActiveTab('requests');
    setSelectedRequestId(match.id);
    setView('conversation');
  }, [initialSelectedRequestId, supportRequests]);

  // Derive selectedRequest from latest props so it updates after refetch
  const selectedRequest = supportRequests.find((r) => r.id === selectedRequestId) || null;
  const activeConversationId = selectedRequest?.conversation?.id;
  const requestUserId = selectedRequest?.user?.id;

  // Clear submitting interstitial once new request arrives in data
  useEffect(() => {
    if (isSubmitting && supportRequests.length > prevRequestCount.current) {
      setIsSubmitting(false);
    }
    prevRequestCount.current = supportRequests.length;
  }, [supportRequests.length, isSubmitting]);

  // Safety timeout: clear submitting after 10s even if refetch hasn't returned
  useEffect(() => {
    if (!isSubmitting) return;
    const timer = setTimeout(() => setIsSubmitting(false), 10000);
    return () => clearTimeout(timer);
  }, [isSubmitting]);

  // ActionCable subscription for real-time messages on the active conversation
  useEffect(() => {
    if (!activeConversationId) return;

    // Reset realtime messages for new conversation
    setRealtimeMessages([]);
    realtimeRef.current = [];

    cableConsumer.current = createConsumer();
    cableSubscription.current = cableConsumer.current.subscriptions.create(
      { channel: 'ChatChannel', room_id: activeConversationId },
      {
        received: (incoming) => {
          if (incoming.type !== 'message') return;
          const msg = incoming.message;
          const realId = String(msg.id);

          // Skip if we already have this message
          if (realtimeRef.current.some((m) => String(m.id) === realId)) return;

          const newMsg = {
            id: realId,
            body: msg.content,
            sender: {
              id: String(msg.sender?.id),
              allowed_name: msg.sender?.allowed_name || msg.sender?.name || 'Support',
            },
            createdAt: msg.sent,
          };

          setRealtimeMessages((prev) => {
            if (prev.some((m) => String(m.id) === realId)) return prev;
            const updated = [...prev, newMsg];
            realtimeRef.current = updated;
            return updated;
          });

          // Clear matching optimistic messages
          setLocalMessages((prev) =>
            prev.filter((lm) => lm.body !== msg.content)
          );
        },
      }
    );

    return () => {
      if (cableSubscription.current) cableSubscription.current.unsubscribe();
      if (cableConsumer.current) cableConsumer.current.disconnect();
    };
  }, [activeConversationId]);

  // Build conversation messages: server + realtime + optimistic, deduped and sorted chronologically
  // (same ordering as admin-v2 SupportRequests detail page — newest at bottom).
  const conversationMessages = (() => {
    if (!selectedRequest) return [];
    const server = selectedRequest.conversation?.personalMessages || [];
    const allIds = new Set(server.map((m) => String(m.id)));
    const rtFiltered = realtimeMessages.filter((rm) => !allIds.has(String(rm.id)));
    rtFiltered.forEach((rm) => allIds.add(String(rm.id)));
    const optFiltered = localMessages.filter((lm) => !allIds.has(String(lm.id)));
    return [...server, ...rtFiltered, ...optFiltered]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  })();

  // Auto-scroll when messages change
  useEffect(() => {
    if (view === 'conversation' && messagesEndRef.current) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [conversationMessages.length, view]);

  // Reconcile: remove optimistic and realtime messages when server data catches up
  const serverMsgCount = selectedRequest?.conversation?.personalMessages?.length || 0;
  useEffect(() => {
    const serverBodies = new Set(
      (selectedRequest?.conversation?.personalMessages || []).map((m) => m.body)
    );
    const serverIds = new Set(
      (selectedRequest?.conversation?.personalMessages || []).map((m) => String(m.id))
    );
    if (localMessages.length > 0) {
      setLocalMessages((prev) => prev.filter((lm) => !serverBodies.has(lm.body)));
    }
    if (realtimeMessages.length > 0) {
      setRealtimeMessages((prev) => {
        const filtered = prev.filter((rm) => !serverIds.has(String(rm.id)));
        realtimeRef.current = filtered;
        return filtered;
      });
    }
  }, [serverMsgCount]);

  const filteredArticles = helpArticles.filter(
    (article) =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (article.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pilot articles (source_type 'pilot_help_center') are pinned at the top
  // The resolver already returns them in logical reading order (created_at asc)
  const pinnedArticles = filteredArticles.filter((a) => a.source_type === 'pilot_help_center');
  const regularArticles = filteredArticles.filter((a) => a.source_type !== 'pilot_help_center');

  const selectedCategory = supportCategories.find((cat) => cat.value === category);

  const handleOpenDrawer = () => {
    setIsOpen(true);
    if (onDrawerOpen) onDrawerOpen();
    if (!isAuthenticated) {
      setView('contact-support');
    } else {
      setView('help-home');
      setActiveTab('help');
    }
  };

  const handleCloseDrawer = () => setIsOpen(false);
  const handleMinimize = () => setIsOpen(false);

  const handleReplayTour = () => {
    setIsOpen(false);
    window.dispatchEvent(new CustomEvent('replay-tour'));
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    setFeedbackGiven(false);
    setView('article-detail');
  };

  const handleBackToHelp = () => {
    setView('help-home');
    setSelectedArticle(null);
  };

  const handleFeedback = (helpful) => {
    setFeedbackGiven(true);
    // Track feedback via Ahoy analytics
    const ahoy = window.ahoy;
    if (ahoy && ahoy.track) {
      ahoy.track('Help Article Feedback', {
        article_id: selectedArticle?.id,
        article_title: selectedArticle?.title,
        article_category: selectedArticle?.category,
        helpful: helpful,
      });
    }
  };

  const handleContactSupport = () => {
    setView('contact-support');
    setCategory('');
    setIssueDescription('');
    setFiles([]);
    setFileError('');
  };

  const handleRequestClick = (request) => {
    setSelectedRequestId(request.id);
    setLocalMessages([]);
    setRealtimeMessages([]);
    realtimeRef.current = [];
    realtimeRef.current = [];
    setView('conversation');
  };

  // File validation: 10MB max, images + PDF only
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_FILE_TYPES = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    'application/pdf',
  ];
  const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.svg,.pdf';
  const [fileError, setFileError] = useState('');

  const validateFiles = (fileList) => {
    const valid = [];
    const errors = [];
    Array.from(fileList).forEach((file) => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported file type. Only images and PDFs are allowed.`);
      } else if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds the 10 MB size limit.`);
      } else {
        valid.push(file);
      }
    });
    if (errors.length > 0) {
      setFileError(errors.join(' '));
    } else {
      setFileError('');
    }
    return valid;
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      const valid = validateFiles(e.target.files);
      setFiles((prev) => [...prev, ...valid]);
    }
    // Reset the input so the same file can be re-selected
    if (e.target) e.target.value = '';
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      const valid = validateFiles(e.dataTransfer.files);
      setFiles((prev) => [...prev, ...valid]);
    }
  };

  const handleSubmitSupport = () => {
    if (isAuthenticated && onSubmitRequest) {
      onSubmitRequest({ category, issueDescription, files });
    } else if (!isAuthenticated && onSubmitLeadRequest) {
      onSubmitLeadRequest({ category, issueDescription, name, email });
    }
    setCategory('');
    setIssueDescription('');
    setName('');
    setEmail('');
    setFiles([]);
    setFileError('');

    if (!isAuthenticated) {
      setLeadSubmitted(true);
    } else {
      // Show submitting interstitial, then switch to My Requests tab
      setIsSubmitting(true);
      setActiveTab('requests');
      setView('help-home');
    }
  };

  const handleSendReply = async () => {
    if ((replyMessage.trim() || replyFiles.length > 0) && selectedRequest && onSendMessage) {
      // Build display body for optimistic message
      const displayBody = replyMessage.trim()
        ? replyMessage + (replyFiles.length > 0 ? ` (+ ${replyFiles.length} file${replyFiles.length > 1 ? 's' : ''})` : '')
        : `📎 Shared ${replyFiles.length} file${replyFiles.length > 1 ? 's' : ''}`;

      const tempId = `temp-${Date.now()}`;
      // Optimistically append the message immediately
      const tempMsg = {
        id: tempId,
        body: displayBody,
        sender: { id: selectedRequest.user?.id || 'me', allowed_name: 'You' },
        createdAt: new Date().toISOString(),
      };
      setLocalMessages((prev) => [...prev, tempMsg]);

      const msgPayload = {
        conversationId: selectedRequest.conversation?.id,
        message: replyMessage || undefined,
        files: replyFiles.length > 0 ? replyFiles : undefined,
      };
      setReplyMessage('');
      setReplyFiles([]);
      setFileError('');

      try {
        await onSendMessage(msgPayload);
        // On success, replace temp message with a confirmed one (remove "Sending..." state)
        setLocalMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, id: `sent-${Date.now()}` } : m))
        );
      } catch (err) {
        // On failure, mark the temp message as failed
        setLocalMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, id: `failed-${Date.now()}`, body: `${m.body} (failed to send)` } : m))
        );
      }
    }
  };

  // Markdown renderer — handles headings, bold, italic, lists, tables, blockquotes, HR
  const renderMarkdown = (content) => {
    if (!content) return '';

    const inlineFmt = (text) =>
      text
        .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold" style="color:var(--succession-text-primary,#111827)">$1</strong>')
        .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded text-sm" style="background:var(--succession-bg-secondary,#f3f4f6)">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline" style="color:var(--succession-accent,#6366f1)">$1</a>');

    const lines = content.split('\n');
    const html = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // ── Table block (consecutive lines starting with |) ──
      if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        const tableLines = [];
        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }
        if (tableLines.length >= 2) {
          const parseRow = (row) => row.split('|').slice(1, -1).map((c) => c.trim());
          const isSep = (row) => /^[\s|:-]+$/.test(row.replace(/\|/g, ''));
          const headerCells = parseRow(tableLines[0]);
          const dataStart = isSep(tableLines[1]) ? 2 : 1;
          let t = '<div class="overflow-x-auto my-4"><table class="w-full border-collapse text-sm" style="border:1px solid var(--succession-border,#e5e7eb)"><thead><tr>';
          headerCells.forEach((c) => { t += `<th class="px-3 py-2 text-left font-semibold text-xs" style="border:1px solid var(--succession-border,#e5e7eb);background:var(--succession-bg-secondary,#f3f4f6);color:var(--succession-text-primary,#111827)">${inlineFmt(c)}</th>`; });
          t += '</tr></thead><tbody>';
          for (let r = dataStart; r < tableLines.length; r++) {
            t += '<tr>';
            parseRow(tableLines[r]).forEach((c) => { t += `<td class="px-3 py-2" style="border:1px solid var(--succession-border,#e5e7eb);color:var(--succession-text-secondary,#6b7280)">${inlineFmt(c)}</td>`; });
            t += '</tr>';
          }
          t += '</tbody></table></div>';
          html.push(t);
        }
        continue;
      }

      // ── Headings ──
      if (line.startsWith('### '))  { html.push(`<h3 class="text-lg font-semibold mt-5 mb-2" style="color:var(--succession-text-primary,#111827)">${inlineFmt(line.slice(4))}</h3>`);  i++; continue; }
      if (line.startsWith('## '))   { html.push(`<h2 class="text-xl font-semibold mt-6 mb-3" style="color:var(--succession-text-primary,#111827)">${inlineFmt(line.slice(3))}</h2>`);  i++; continue; }
      if (line.startsWith('# '))    { html.push(`<h1 class="text-2xl font-bold mt-6 mb-4" style="color:var(--succession-text-primary,#111827)">${inlineFmt(line.slice(2))}</h1>`);     i++; continue; }

      // ── Horizontal rule ──
      if (line.trim() === '---' || line.trim() === '***' || line.trim() === '___') { html.push('<hr class="my-6" style="border-color:var(--succession-border,#e5e7eb)" />'); i++; continue; }

      // ── Blockquote ──
      if (line.startsWith('> ')) { html.push(`<blockquote class="pl-4 my-3" style="border-left:3px solid var(--succession-accent,#6366f1);color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line.slice(2))}</blockquote>`); i++; continue; }

      // ── Unordered list item ──
      if (line.match(/^\s*- /)) { html.push(`<li class="ml-6 mb-1 list-disc" style="color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line.replace(/^\s*- /, ''))}</li>`); i++; continue; }

      // ── Ordered list item ──
      if (line.match(/^\s*\d+\.\s/)) { html.push(`<li class="ml-6 mb-1 list-decimal" style="color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line.replace(/^\s*\d+\.\s/, ''))}</li>`); i++; continue; }

      // ── Empty line ──
      if (line.trim() === '') { html.push('<br />'); i++; continue; }

      // ── Paragraph ──
      html.push(`<p class="mb-3 leading-7" style="color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line)}</p>`);
      i++;
    }

    return html.join('');
  };

  const headerStyle = {
    borderBottom: '1px solid var(--succession-border, #e5e7eb)',
    backgroundColor: 'var(--succession-bg-primary, #ffffff)',
  };
  const headerTitleStyle = { color: 'var(--succession-text-primary, #111827)' };
  const headerBtnClass = 'p-1 rounded transition-colors duration-300 shrink-0';

  /* ─── Header ─── */
  const renderHeader = () => {
    if (view === 'article-detail' && selectedArticle) {
      return (
        <div className="flex items-center justify-between p-4" style={headerStyle}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button onClick={handleBackToHelp} className={headerBtnClass} style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <h2 className="font-semibold truncate" style={headerTitleStyle}>{selectedArticle.title}</h2>
          </div>
          <button onClick={handleMinimize} className={headerBtnClass} style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
      );
    }

    if (view === 'contact-support') {
      return (
        <div className="flex items-center justify-between p-4" style={headerStyle}>
          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <button onClick={handleBackToHelp} className={headerBtnClass} style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
            )}
            <h2 className="font-semibold" style={headerTitleStyle}>{intl.formatMessage({ id: 'succession.support_help_drawer.contact_support' })}</h2>
          </div>
          <button onClick={handleMinimize} className={headerBtnClass} style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
      );
    }

    if (view === 'conversation' && selectedRequest) {
      return (
        <div className="flex items-center justify-between p-4" style={headerStyle}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button onClick={() => { setView('help-home'); setSelectedRequestId(null); setLocalMessages([]); setRealtimeMessages([]); realtimeRef.current = []; setActiveTab('requests'); }} className={headerBtnClass} style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <h2 className="font-semibold truncate" style={headerTitleStyle}>{formatCategoryName(selectedRequest.subject || selectedRequest.friendlyName)}</h2>
          </div>
          <button onClick={handleMinimize} className={headerBtnClass} style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-4" style={headerStyle}>
        <h2 className="text-lg font-semibold" style={headerTitleStyle}>{intl.formatMessage({ id: 'succession.support_help_drawer.how_can_we_help' })}</h2>
        <button onClick={handleMinimize} className={headerBtnClass} style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
      </div>
    );
  };

  /* ─── Help Home ─── */
  const renderHelpHome = () => (
    <div className="flex flex-col h-full">
      <div className="p-4" style={{ borderBottom: '1px solid var(--succession-border, #e5e7eb)' }}>
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={intl.formatMessage({ id: 'succession.support_help_drawer.search_placeholder' })}
          variant="outline"
          size="md"
        />
      </div>

      {/* Article list: pinned pilot articles first, then regular */}
      <div className="overflow-y-auto p-4 space-y-3" style={{ maxHeight: '340px' }}>
        {pinnedArticles.length > 0 && (
          <>
            <div className="flex items-center gap-2 pb-1">
              <svg className="w-3.5 h-3.5" style={{ color: 'var(--succession-primary, #2563eb)' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a.75.75 0 01.75.75v.258a33.186 33.186 0 016.668.83.75.75 0 01-.336 1.461 31.28 31.28 0 00-1.103-.232l1.702 7.545a.75.75 0 01-.387.832A4.981 4.981 0 0115 14c-.825 0-1.606-.2-2.294-.556a.75.75 0 01-.387-.832l1.77-7.849a31.743 31.743 0 00-3.339-.254v11.505a20.01 20.01 0 013.78.501.75.75 0 11-.339 1.462A18.558 18.558 0 0010 17.5a18.558 18.558 0 00-4.191.477.75.75 0 01-.339-1.462 20.01 20.01 0 013.78-.501V4.509c-1.129.026-2.243.112-3.34.254l1.771 7.85a.75.75 0 01-.387.83A4.981 4.981 0 015 14a4.981 4.981 0 01-2.294-.556.75.75 0 01-.387-.832L4.02 5.067c-.37.07-.738.148-1.103.232a.75.75 0 01-.336-1.462 33.186 33.186 0 016.668-.829V2.75A.75.75 0 0110 2zM5 12.662l-1.395-6.177C4.442 6.34 5.517 6.5 6.395 6.841L5 12.662zm10 0l-1.395-5.82A13.93 13.93 0 0115.001 6.485L16.395 12.662z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--succession-primary, #2563eb)' }}>
                {intl.formatMessage({ id: 'succession.support_help_drawer.pilot_guide' })}
              </span>
            </div>
            {pinnedArticles.map((article) => (
              <button
                key={article.id}
                onClick={() => handleArticleClick(article)}
                className="w-full text-left p-4 rounded-lg transition-colors duration-300"
                style={{
                  border: '1px solid var(--succession-primary, #2563eb)',
                  backgroundColor: 'var(--succession-bg-primary, #ffffff)',
                  borderLeft: '3px solid var(--succession-primary, #2563eb)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-secondary, #f9fafb)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-primary, #ffffff)'; }}
              >
                <div className="flex gap-3">
                  <div
                    className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--succession-primary, #2563eb) 10%, transparent)' }}
                  >
                    <SvgIcon name={article.icon || 'book-open'} className="w-5 h-5" style={{ color: 'var(--succession-primary, #2563eb)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1" style={{ color: 'var(--succession-text-primary, #111827)' }}>{article.title}</h3>
                    <p className="text-sm line-clamp-2" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{article.excerpt || article.description}</p>
                  </div>
                  <svg className="shrink-0 w-4 h-4 mt-0.5" style={{ color: '#ef4444', opacity: 0.5 }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                  </svg>
                </div>
              </button>
            ))}
            {regularArticles.length > 0 && (
              <div style={{ borderTop: '1px solid var(--succession-border, #e5e7eb)', margin: '4px 0' }} />
            )}
          </>
        )}
        {regularArticles.map((article) => (
          <button
            key={article.id}
            onClick={() => handleArticleClick(article)}
            className="w-full text-left p-4 rounded-lg transition-colors duration-300"
            style={{
              border: '1px solid var(--succession-border, #e5e7eb)',
              backgroundColor: 'var(--succession-bg-primary, #ffffff)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-secondary, #f9fafb)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-primary, #ffffff)'; }}
          >
            <div className="flex gap-3">
              <div
                className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--succession-bg-tertiary, #f3f4f6)' }}
              >
                <SvgIcon name={article.icon || 'file-text'} className="w-5 h-5" style={{ color: 'var(--succession-text-secondary, #6b7280)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold mb-1" style={{ color: 'var(--succession-text-primary, #111827)' }}>{article.title}</h3>
                <p className="text-sm line-clamp-2" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{article.excerpt || article.description}</p>
              </div>
            </div>
          </button>
        ))}
        {filteredArticles.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.no_articles_found' }, { query: searchQuery })}</p>
          </div>
        )}
      </div>

      <div
        className="p-4 space-y-3 shrink-0"
        style={{
          borderTop: '1px solid var(--succession-border, #e5e7eb)',
          backgroundColor: 'var(--succession-bg-primary, #ffffff)',
        }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={handleContactSupport}
            className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md text-white transition-opacity duration-300"
            style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
            {intl.formatMessage({ id: 'succession.support_help_drawer.contact_us' })}
          </button>
          <a
            href="/succession/help"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm px-3 py-2 rounded transition-colors duration-300"
            style={{ color: 'var(--succession-primary, #5489B6)' }}
          >
            {intl.formatMessage({ id: 'succession.support_help_drawer.view_on_help_center' })}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
          </a>
        </div>
        <div className="flex items-center justify-center">
          <ReplayTourButton onReplayTour={handleReplayTour} />
        </div>
      </div>
    </div>
  );

  /* ─── Article Detail ─── */
  const renderArticleDetail = () => {
    if (!selectedArticle) return null;
    const body = selectedArticle.mdBody || selectedArticle.md_body || selectedArticle.content || '';
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto p-6 prose prose-sm max-w-none min-h-0">
          <div style={{ color: 'var(--succession-text-primary, #111827)' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }} />
        </div>
        <div className="p-4 space-y-3 shrink-0" style={{ borderTop: '1px solid var(--succession-border, #e5e7eb)', backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
          {!feedbackGiven ? (
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.was_helpful' })}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleFeedback(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors duration-300"
                  style={{ border: '1px solid var(--succession-border, #e5e7eb)', color: 'var(--succession-text-secondary, #6b7280)' }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21H7a2 2 0 01-2-2v-7a2 2 0 012-2h.5L12 3l2 7z" /></svg>
                  {intl.formatMessage({ id: 'succession.support_help_drawer.yes' })}
                </button>
                <button
                  onClick={() => handleFeedback(false)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors duration-300"
                  style={{ border: '1px solid var(--succession-border, #e5e7eb)', color: 'var(--succession-text-secondary, #6b7280)' }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.737 3H17a2 2 0 012 2v7a2 2 0 01-2 2h-.5L12 21l-2-7z" /></svg>
                  {intl.formatMessage({ id: 'succession.support_help_drawer.no' })}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--succession-bg-secondary, #f9fafb)' }}>
              <p className="text-sm" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.feedback_thank_you' })}</p>
            </div>
          )}
          <button
            onClick={handleContactSupport}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium px-4 py-2 rounded-md text-white transition-opacity duration-300"
            style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
            {intl.formatMessage({ id: 'succession.support_help_drawer.contact_us' })}
          </button>
        </div>
      </div>
    );
  };

  const inputStyle = {
    backgroundColor: 'var(--succession-bg-primary, #ffffff)',
    color: 'var(--succession-text-primary, #111827)',
    border: '1px solid var(--succession-border, #e5e7eb)',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
  };
  const labelStyle = { color: 'var(--succession-text-primary, #111827)' };

  /* ─── Contact Support Form ─── */
  const renderContactSupport = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!isAuthenticated && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium" style={labelStyle}>{intl.formatMessage({ id: 'succession.support_help_drawer.name_label' })}</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={intl.formatMessage({ id: 'succession.support_help_drawer.name_placeholder' })} className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2" style={{ ...inputStyle, '--tw-ring-color': 'var(--succession-focus-ring, rgba(84,137,182,0.5))' }} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" style={labelStyle}>{intl.formatMessage({ id: 'succession.support_help_drawer.email_label' })}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your.email@example.com" className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2" style={{ ...inputStyle, '--tw-ring-color': 'var(--succession-focus-ring, rgba(84,137,182,0.5))' }} />
            </div>
          </>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium" style={labelStyle}>{intl.formatMessage({ id: 'succession.support_help_drawer.category_label' })}</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2" style={{ ...inputStyle, '--tw-ring-color': 'var(--succession-focus-ring, rgba(84,137,182,0.5))' }}>
            <option value="">{intl.formatMessage({ id: 'succession.support_help_drawer.category_select' })}</option>
            {effectiveCategories.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        {selectedCategory?.hint && (
          <div className="flex gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(var(--succession-primary-rgb, 84,137,182), 0.08)', border: '1px solid rgba(var(--succession-primary-rgb, 84,137,182), 0.25)' }}>
            <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--succession-primary, #5489B6)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="text-sm" style={{ color: 'var(--succession-text-primary, #111827)' }}>{selectedCategory.hint}</p>
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium" style={labelStyle}>{intl.formatMessage({ id: 'succession.support_help_drawer.describe_issue' })}</label>
          <textarea value={issueDescription} onChange={(e) => setIssueDescription(e.target.value)} placeholder={intl.formatMessage({ id: 'succession.support_help_drawer.issue_placeholder' })} rows={5} className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 resize-none" style={{ ...inputStyle, '--tw-ring-color': 'var(--succession-focus-ring, rgba(84,137,182,0.5))' }} />
        </div>

        {isAuthenticated && (
          <div className="space-y-2">
            <label className="text-sm font-medium" style={labelStyle}>{intl.formatMessage({ id: 'succession.support_help_drawer.attach_files' })}</label>
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-lg p-8 text-center cursor-pointer transition-colors duration-300"
              style={{ border: '2px dashed var(--succession-border, #d1d5db)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-secondary, #f9fafb)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <input ref={fileInputRef} type="file" multiple accept={ALLOWED_EXTENSIONS} onChange={handleFileSelect} className="hidden" />
              <svg className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--succession-text-muted, #9ca3af)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
              <p className="font-medium mb-1" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.drag_drop' })}</p>
              <p className="text-sm" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.file_type_hint' })}</p>
            </div>
            {files.length > 0 && (
              <div className="space-y-2 mt-3">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: 'var(--succession-bg-tertiary, #f3f4f6)' }}>
                    <div className="flex-1 min-w-0 mr-2">
                      <span className="text-sm truncate block" style={{ color: 'var(--succession-text-primary, #111827)' }}>{file.name}</span>
                      <span className="text-xs" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, i) => i !== index)); }} className="p-1 rounded transition-colors duration-300" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {fileError && (
              <p className="text-xs mt-2" style={{ color: '#ef4444' }}>{fileError}</p>
            )}
          </div>
        )}
      </div>

      <div className="p-4" style={{ borderTop: '1px solid var(--succession-border, #e5e7eb)', backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
        <button
          onClick={handleSubmitSupport}
          disabled={!category || !issueDescription || (!isAuthenticated && (!name || !email))}
          className="w-full py-2 px-4 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-300"
          style={{ backgroundColor: 'var(--succession-primary, #5489B6)', '--tw-ring-color': 'var(--succession-focus-ring, rgba(84,137,182,0.5))' }}
          onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          {intl.formatMessage({ id: 'succession.support_help_drawer.send_message' })}
        </button>
      </div>
    </div>
  );

  /* ─── My Requests ─── */
  const renderMyRequests = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '400px' }}>
        {isSubmitting && (
          <div
            className="flex items-center gap-3 p-4 rounded-lg animate-pulse"
            style={{ border: '1px solid var(--succession-border, #e5e7eb)', backgroundColor: 'var(--succession-bg-secondary, #f9fafb)' }}
          >
            <div
              className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: 'var(--succession-primary, #5489B6)', borderTopColor: 'transparent' }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
              {intl.formatMessage({ id: 'succession.support_help_drawer.submitting' })}
            </span>
          </div>
        )}
        {supportRequests.length === 0 && !isSubmitting && (
          <div className="text-center py-12">
            <p className="text-sm" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.no_requests' })}</p>
          </div>
        )}
        {supportRequests.map((request) => (
          <button
            key={request.id}
            onClick={() => handleRequestClick(request)}
            className="w-full text-left p-4 rounded-lg transition-colors duration-300"
            style={{ border: '1px solid var(--succession-border, #e5e7eb)', backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-secondary, #f9fafb)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-primary, #ffffff)'; }}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold flex-1" style={{ color: 'var(--succession-text-primary, #111827)' }}>{formatCategoryName(request.subject || request.friendlyName)}</h3>
              <StatusBadge status={request.status} />
            </div>
            <p className="text-sm line-clamp-2" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{request.lastMessage || request.leadMessage}</p>
            <p className="text-xs mt-2" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>
              {new Date(request.createdAt).toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </p>
          </button>
        ))}
      </div>
      <div className="p-4 space-y-3 shrink-0" style={{ borderTop: '1px solid var(--succession-border, #e5e7eb)', backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
        <button
          onClick={handleContactSupport}
          className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium text-white transition-opacity duration-300"
          style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
          {intl.formatMessage({ id: 'succession.support_help_drawer.new_support_request' })}
        </button>
        <div className="flex items-center justify-center">
          <ReplayTourButton onReplayTour={handleReplayTour} />
        </div>
      </div>
    </div>
  );

  /* ─── Conversation View ─── */
  const renderConversation = () => {
    if (!selectedRequest) return null;

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Request summary card */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--succession-bg-tertiary, #f3f4f6)' }}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--succession-text-primary, #111827)' }}>{formatCategoryName(selectedRequest.category)}</p>
                <p className="text-xs" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{new Date(selectedRequest.createdAt).toLocaleString()}</p>
              </div>
              <StatusBadge status={selectedRequest.status} />
            </div>
          </div>

          {/* Messages (server + optimistic) */}
          {conversationMessages.map((msg, idx) => {
            const isUser = (msg.sender?.id || msg.sender) === selectedRequest.user?.id;
            const isTemp = msg.id?.toString().startsWith('temp-');
            const isFailed = msg.id?.toString().startsWith('failed-');
            return (
              <div key={msg.id || idx} className={isUser ? '' : 'ml-8'}>
                <div
                  className="p-4 rounded-lg"
                  style={{
                    backgroundColor: isFailed
                      ? 'rgba(239,68,68,0.08)'
                      : isUser
                        ? 'var(--succession-bg-tertiary, #f3f4f6)'
                        : 'rgba(var(--succession-primary-rgb, 84,137,182), 0.08)',
                    opacity: isTemp ? 0.7 : 1,
                  }}
                >
                  <p className="text-xs mb-2" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{isUser ? intl.formatMessage({ id: 'succession.support_help_drawer.you' }) : (msg.sender?.allowed_name || intl.formatMessage({ id: 'succession.support_help_drawer.support_team' }))}</p>
                  <p className="text-sm" style={{ color: isFailed ? '#ef4444' : 'var(--succession-text-primary, #111827)' }}>{msg.body}</p>
                  {msg.documents && msg.documents.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {msg.documents.map((doc) => (
                        <a
                          key={doc.id}
                          href={doc.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-xs rounded px-2 py-1.5 transition-colors"
                          style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)', border: '1px solid var(--succession-border, #d1d5db)', color: 'var(--succession-accent, #6366f1)' }}
                        >
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className="truncate">{doc.name}</span>
                        </a>
                      ))}
                    </div>
                  )}
                  {msg.createdAt && (
                    <p className="text-xs mt-2" style={{ color: isFailed ? '#ef4444' : 'var(--succession-text-muted, #9ca3af)' }}>
                      {isTemp ? intl.formatMessage({ id: 'succession.support_help_drawer.sending' }) : isFailed ? intl.formatMessage({ id: 'succession.support_help_drawer.failed_to_send' }) : new Date(msg.createdAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {conversationMessages.length === 0 && (
            <div className="p-4 rounded-lg ml-8" style={{ backgroundColor: 'rgba(var(--succession-primary-rgb, 84,137,182), 0.08)' }}>
              <p className="text-xs mb-2" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.support_team' })}</p>
              <p className="text-sm" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.support_help_drawer.thank_you_reviewing' })}</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="p-4" style={{ borderTop: '1px solid var(--succession-border, #e5e7eb)', backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
          {fileError && (
            <p className="text-xs mb-2" style={{ color: '#ef4444' }}>{fileError}</p>
          )}
          {replyFiles.length > 0 && (
            <div className="mb-2 space-y-1">
              {replyFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--succession-bg-tertiary, #f3f4f6)' }}>
                  <span className="truncate" style={{ color: 'var(--succession-text-primary, #111827)' }}>{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                  <button onClick={() => setReplyFiles(replyFiles.filter((_, i) => i !== idx))} className="ml-2 p-0.5 rounded" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendReply(); }}
              placeholder={intl.formatMessage({ id: 'succession.support_help_drawer.type_message' })}
              className="flex-1 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2"
              style={{ ...inputStyle, '--tw-ring-color': 'var(--succession-focus-ring, rgba(84,137,182,0.5))' }}
            />
            <input ref={replyFileInputRef} type="file" multiple accept={ALLOWED_EXTENSIONS} onChange={(e) => { if (e.target.files) { const valid = validateFiles(e.target.files); if (valid.length > 0) setReplyFiles((prev) => [...prev, ...valid]); } e.target.value = ''; }} className="hidden" />
            <button
              onClick={() => replyFileInputRef.current?.click()}
              className="px-2 py-2 rounded-md transition-opacity duration-300"
              style={{ color: 'var(--succession-text-secondary, #6b7280)', border: '1px solid var(--succession-border, #d1d5db)' }}
              title="Attach file"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
            </button>
            <button
              onClick={handleSendReply}
              disabled={!replyMessage.trim() && replyFiles.length === 0}
              className="px-3 py-2 text-white rounded-md transition-opacity duration-300 disabled:opacity-50"
              style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
              onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ─── Lead Submitted Confirmation ─── */
  const renderLeadConfirmation = () => (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
        style={{ backgroundColor: 'rgba(var(--succession-success-rgb, 16,185,129), 0.1)' }}
      >
        <svg className="w-8 h-8" style={{ color: 'var(--succession-success, #10b981)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--succession-text-primary, #111827)' }}>
        {intl.formatMessage({ id: 'succession.support_help_drawer.message_sent' })}
      </h3>
      <p className="text-sm mb-6" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
        {intl.formatMessage({ id: 'succession.support_help_drawer.message_sent_body' })}
      </p>
      <button
        onClick={() => setLeadSubmitted(false)}
        className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md text-white transition-opacity duration-300"
        style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        {intl.formatMessage({ id: 'succession.support_help_drawer.send_another' })}
      </button>
    </div>
  );

  /* ─── Content Router ─── */
  const renderContent = () => {
    if (!isAuthenticated) return leadSubmitted ? renderLeadConfirmation() : renderContactSupport();
    if (view === 'article-detail') return renderArticleDetail();
    if (view === 'contact-support') return renderContactSupport();
    if (view === 'conversation') return renderConversation();

    // Default: tabbed view (Help / My Requests)
    return (
      <div className="flex flex-col h-full">
        <div className="flex mx-4 my-2 rounded-lg p-1" style={{ backgroundColor: 'var(--succession-bg-tertiary, #f3f4f6)' }}>
          <button
            onClick={() => setActiveTab('help')}
            className="flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-colors duration-300"
            style={{
              backgroundColor: activeTab === 'help' ? 'var(--succession-bg-primary, #ffffff)' : 'transparent',
              color: activeTab === 'help' ? 'var(--succession-text-primary, #111827)' : 'var(--succession-text-muted, #9ca3af)',
              boxShadow: activeTab === 'help' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}
          >
            {intl.formatMessage({ id: 'succession.support_help_drawer.tab_help' })}
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className="flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-colors duration-300"
            style={{
              backgroundColor: activeTab === 'requests' ? 'var(--succession-bg-primary, #ffffff)' : 'transparent',
              color: activeTab === 'requests' ? 'var(--succession-text-primary, #111827)' : 'var(--succession-text-muted, #9ca3af)',
              boxShadow: activeTab === 'requests' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}
          >
            {intl.formatMessage({ id: 'succession.support_help_drawer.tab_requests' })}
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center ml-1.5 text-xs font-bold text-white rounded-full"
                style={{
                  backgroundColor: '#ef4444',
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 5px',
                  lineHeight: '18px',
                }}
              >
                {unreadCount}
              </span>
            )}
          </button>
        </div>
        <div className="flex-1 overflow-hidden flex flex-col" style={{ height: 'calc(100% - 3rem)' }}>
          {activeTab === 'help' ? renderHelpHome() : renderMyRequests()}
        </div>
      </div>
    );
  };

  /* ─── Main Render ─── */
  return (
    <>
      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleOpenDrawer}
          className="rounded-full w-14 h-14 shadow-lg text-white flex items-center justify-center transition-opacity duration-300 relative"
          style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 text-white rounded-full text-xs flex items-center justify-center font-semibold" style={{ backgroundColor: 'var(--succession-error, #ef4444)' }}>
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Drawer */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-20 z-40 transition-opacity" onClick={handleCloseDrawer} />

          {/* Drawer Panel */}
          <div
            className="fixed bottom-0 right-0 w-full md:w-120 md:max-w-md lg:max-w-lg shadow-2xl z-50 flex flex-col rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none overflow-hidden"
            style={{
              maxHeight: '85vh',
              maxWidth: '480px',
              animation: 'supportDrawerSlideIn 0.3s ease-out',
              backgroundColor: 'var(--succession-bg-primary, #ffffff)',
              borderLeft: '1px solid var(--succession-border, #e5e7eb)',
            }}
          >
            {renderHeader()}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              {renderContent()}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes supportDrawerSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @media (max-width: 767px) {
          @keyframes supportDrawerSlideIn {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default SupportHelpDrawer;
